import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, Plus, X, MapPin, Share2, Copy, Trophy, Filter } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CLUBS } from "@/data/clubs";
import { GROUNDS, NINETY_TWO_LEAGUES } from "@/data/grounds";
import BottomNav from "@/components/app/BottomNav";

const clubColor = (name) => CLUBS.find(c => c.name === name)?.color || "#152A55";
const clubLeague = (name) => CLUBS.find(c => c.name === name)?.league || "";
const groundOf = (name) => GROUNDS[name] || "Home ground";
const initialsOf = (name) => (name || "?").split(" ").filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase();

// Ordered list of leagues the picker groups clubs under. English 92 first, then
// the National League tiers, then Scottish Prem — matches the on-boarding grouping.
const LEAGUE_ORDER = [
  "Premier League", "Championship", "League One", "League Two",
  "National League", "National League North", "National League South",
  "Scottish Premiership",
];

// Owner view when :userId is missing or matches the logged-in user; otherwise
// a read-only public passport of somebody else's grounds.
export default function Grounds() {
  const { userId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isOwnerRoute = !userId || (user && userId === user.id);

  const [state, setState] = useState({ loading: true, count: 0, visits: [], target: null });
  const [crests, setCrests] = useState({});
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [gRes, cRes] = await Promise.all([
          isOwnerRoute
            ? api.get("/grounds/me")
            : api.get(`/grounds/user/${userId}`),
          api.get("/grounds/crests"),
        ]);
        setState({
          loading: false,
          count: gRes.data.count,
          visits: gRes.data.visits || [],
          target: gRes.data.user || null,
        });
        setCrests(cRes.data || {});
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load grounds book");
        setState(s => ({ ...s, loading: false }));
      }
    })();
  }, [userId, isOwnerRoute]);

  const addGround = async (club) => {
    try {
      const { data } = await api.post("/grounds/me", { club_name: club });
      setState(s => ({
        ...s,
        count: data.count,
        visits: [{ club_name: data.club_name, added_at: new Date().toISOString() }, ...s.visits.filter(v => v.club_name !== data.club_name)],
      }));
      toast.success(`${club} added to your Grounds Book`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add ground");
    }
  };
  const removeGround = async (club) => {
    try {
      const { data } = await api.delete("/grounds/me", { data: { club_name: club } });
      setState(s => ({
        ...s,
        count: data.count,
        visits: s.visits.filter(v => v.club_name !== club),
      }));
      toast.success(`${club} removed`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove ground");
    }
  };

  const shareLink = useMemo(() => {
    const targetId = state.target?.id || user?.id;
    if (!targetId) return "";
    return `${window.location.origin}/grounds/${targetId}`;
  }, [state.target, user]);

  const doShare = async () => {
    const text = isOwnerRoute
      ? `Ticked off ${state.count} ground${state.count === 1 ? "" : "s"} on my Footymatch Grounds Book. Chasing the 92 — where should I go next?`
      : `${state.target?.name || "This fan"} has ticked off ${state.count} ground${state.count === 1 ? "" : "s"} on Footymatch.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Footymatch — Grounds Book", text, url: shareLink });
        return;
      } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied — send it to the lads");
    } catch {
      toast.error("Couldn't copy — long-press the link to share");
    }
  };

  const visitedSet = useMemo(() => new Set(state.visits.map(v => v.club_name)), [state.visits]);
  const ownerClub = (isOwnerRoute ? user?.profile?.club : state.target?.club) || "";
  const bannerColor = clubColor(ownerClub);
  const ninetyTwoTotal = 92;
  const ninetyTwoDone = state.visits.filter(v => NINETY_TWO_LEAGUES.has(clubLeague(v.club_name))).length;

  return (
    <div className="min-h-screen bg-footy-bg text-white pb-24 grain" data-testid="grounds-page">
      <div className="relative overflow-hidden fm-topbar" style={{ background: `linear-gradient(160deg, ${bannerColor}, #061022 85%)` }}>
       <div className="h-40 relative">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "repeating-linear-gradient(45deg, #fff 0 2px, transparent 2px 14px)",
        }} />
        <button onClick={() => nav(-1)} data-testid="grounds-back"
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center">
          <ArrowLeft size={18} />
        </button>
        <button onClick={doShare} data-testid="grounds-share-btn"
          className="absolute top-4 right-4 px-4 h-10 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
          <Share2 size={14} /> Share
        </button>
        <div className="absolute bottom-4 left-5 right-5">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/70">Grounds Book</div>
          <div className="font-display text-4xl uppercase tracking-tight leading-none mt-1">
            {isOwnerRoute ? "Your ground-hopper log" : `${state.target?.name || "Footymatch"}'s grounds`}
          </div>
        </div>
       </div>
      </div>

      <div className="max-w-md mx-auto px-5 pt-5">
        {/* Progress strip — the classic 92 Club counter */}
        <div className="footy-card p-4 flex items-center gap-4" data-testid="grounds-progress">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 flex-none">
            <Trophy size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-2xl uppercase leading-none">
              <span data-testid="grounds-count">{state.count}</span>
              <span className="text-slate-500 text-xl"> grounds ticked</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {ninetyTwoDone}/{ninetyTwoTotal} of the 92 · keep chipping away.
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full bg-yellow-400/70 transition-all"
                style={{ width: `${Math.min(100, (ninetyTwoDone / ninetyTwoTotal) * 100)}%` }} />
            </div>
          </div>
        </div>

        {/* Crest grid */}
        {state.loading ? (
          <div className="mt-6 grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : state.visits.length === 0 ? (
          <EmptyState onAdd={() => setPicking(true)} isOwner={isOwnerRoute} />
        ) : (
          <div className="mt-6 grid grid-cols-3 gap-3" data-testid="grounds-grid">
            {state.visits.map(v => (
              <CrestTile
                key={v.club_name}
                club={v.club_name}
                crestUrl={crests[v.club_name.toLowerCase()]}
                onRemove={isOwnerRoute ? () => removeGround(v.club_name) : null}
              />
            ))}
            {isOwnerRoute && (
              <button onClick={() => setPicking(true)} data-testid="grounds-add-tile"
                className="aspect-square rounded-2xl border-2 border-dashed border-white/15 hover:border-footy-red/60 hover:bg-footy-red/5 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-footy-red transition-colors">
                <Plus size={22} />
                <span className="text-[10px] uppercase tracking-widest font-bold">Add ground</span>
              </button>
            )}
          </div>
        )}

        {/* Share link row */}
        {state.count > 0 && (
          <div className="mt-6 footy-card p-4" data-testid="grounds-share-row">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 mb-2">
              <Share2 size={12} /> Shareable link
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-xs font-mono px-2 py-1.5 rounded-md bg-black/40 border border-white/10 text-slate-300" data-testid="grounds-share-link">{shareLink}</code>
              <button
                onClick={async () => {
                  try { await navigator.clipboard.writeText(shareLink); toast.success("Link copied"); }
                  catch { toast.error("Copy failed"); }
                }}
                className="text-slate-400 hover:text-white p-1.5" title="Copy" data-testid="grounds-share-copy">
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {picking && isOwnerRoute && (
        <GroundPicker
          alreadyVisited={visitedSet}
          crests={crests}
          onPick={async (club) => { await addGround(club); }}
          onClose={() => setPicking(false)}
        />
      )}
      {isOwnerRoute && <BottomNav />}
    </div>
  );
}

function EmptyState({ onAdd, isOwner }) {
  return (
    <div className="mt-8 footy-card p-6 text-center" data-testid="grounds-empty">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-footy-red/10 border border-footy-red/40 text-footy-red flex items-center justify-center">
        <MapPin size={22} />
      </div>
      <div className="font-display text-2xl uppercase tracking-tight mt-4">
        {isOwner ? "Start ticking off grounds" : "No grounds yet"}
      </div>
      <p className="text-sm text-slate-400 mt-2">
        {isOwner
          ? "Every ground you've ever been to lives here. Tap Add and pick your first."
          : "This fan hasn't logged any grounds yet."}
      </p>
      {isOwner && (
        <button onClick={onAdd} className="footy-btn-primary mt-5 w-full" data-testid="grounds-empty-add">
          <Plus size={16} /> Add my first ground
        </button>
      )}
    </div>
  );
}

function CrestTile({ club, crestUrl, onRemove }) {
  const color = clubColor(club);
  const ground = groundOf(club);
  return (
    <div
      className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-footy-card flex flex-col items-center justify-center px-2 group"
      style={{ background: `linear-gradient(160deg, ${color}22 0%, #0b0f1a 90%)` }}
      data-testid={`ground-tile-${club.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
    >
      <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center overflow-hidden shadow-inner">
        {crestUrl ? (
          <img src={crestUrl} alt={club} className="w-11 h-11 object-contain" />
        ) : (
          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-display text-lg"
            style={{ background: color }}>
            {initialsOf(club)}
          </div>
        )}
      </div>
      <div className="mt-2 text-[9px] uppercase tracking-widest font-bold text-white text-center leading-tight line-clamp-2">
        {club}
      </div>
      <div className="text-[8px] text-slate-400 mt-0.5 uppercase tracking-widest line-clamp-1">
        {ground}
      </div>
      {onRemove && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100"
          title="Remove"
          data-testid={`ground-remove-${club.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

function GroundPicker({ alreadyVisited, crests, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [only92, setOnly92] = useState(true);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return CLUBS.filter(c => {
      if (only92 && !NINETY_TWO_LEAGUES.has(c.league)) return false;
      if (!norm) return true;
      const g = (GROUNDS[c.name] || "").toLowerCase();
      return c.name.toLowerCase().includes(norm) || g.includes(norm);
    });
  }, [q, only92]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(c => {
      if (!map.has(c.league)) map.set(c.league, []);
      map.get(c.league).push(c);
    });
    return LEAGUE_ORDER
      .filter(l => map.has(l))
      .map(l => ({ league: l, clubs: map.get(l) }));
  }, [filtered]);

  const pick = async (club) => {
    if (alreadyVisited.has(club)) { toast.info(`${club} already in your book`); return; }
    setBusy(true);
    try { await onPick(club); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="grounds-picker"
    >
      <div
        className="w-full sm:max-w-md h-[85vh] sm:h-[80vh] bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 p-4 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-footy-red/15 text-footy-red flex items-center justify-center">
            <Plus size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl uppercase tracking-wide leading-none">Add a ground</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Pick any club whose ground you&apos;ve been to</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-white/5 text-slate-300 flex items-center justify-center" data-testid="grounds-picker-close">
            <X size={18} />
          </button>
        </header>
        <div className="p-4 space-y-3 border-b border-white/10">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search club or ground…"
              className="footy-input pl-9"
              data-testid="grounds-picker-search"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none" data-testid="grounds-picker-92-toggle">
            <input type="checkbox" checked={only92} onChange={(e) => setOnly92(e.target.checked)} className="accent-footy-red" />
            <Filter size={12} className="text-footy-red" />
            <span>Show only the 92 (Prem + EFL)</span>
          </label>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3">
          {grouped.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-8">No clubs match &ldquo;{q}&rdquo;.</p>
          )}
          {grouped.map(({ league, clubs }) => (
            <div key={league} className="mb-4">
              <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">{league}</div>
              <div className="grid grid-cols-1 gap-1">
                {clubs.map(c => {
                  const done = alreadyVisited.has(c.name);
                  const crest = crests[c.name.toLowerCase()];
                  return (
                    <button
                      key={c.name}
                      onClick={() => pick(c.name)}
                      disabled={busy || done}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${done ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200" : "hover:bg-white/5"}`}
                      data-testid={`grounds-picker-item-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center overflow-hidden flex-none">
                        {crest ? (
                          <img src={crest} alt="" className="w-7 h-7 object-contain" />
                        ) : (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                            style={{ background: c.color }}>
                            {initialsOf(c.name)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{c.name}</div>
                        <div className="text-[10px] text-slate-400 truncate uppercase tracking-widest">{groundOf(c.name)}</div>
                      </div>
                      {done ? (
                        <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">Ticked</span>
                      ) : (
                        <Plus size={14} className="text-slate-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
